import type { TraceAction, TraceGroup } from '@chatsdet/trace-tools';
import type { PlaybookStep } from '@chatsdet/types';
import type { RunCodeToolCall, StrictParsedRunCode } from './step-linker.js';

export type ProvenanceClass = 'user' | 'internal';

export interface CandidateAction {
  index: number;
  action: TraceAction;
  method: string;
  role: 'terminal' | 'assistive' | 'observation' | 'unsupported';
}

export type GroupScopeSignal =
  | 'group_scope:playbook-step'
  | 'group_scope:malformed'
  | 'group_scope:run-code-executed'
  | 'group_scope:join-mismatch';

export type PlaybookStepScope =
  | { status: 'none' }
  | { status: 'malformed' }
  | { status: 'applied'; candidateIndicesByStep: Map<number, Set<number>> };

export type RunCodeGroupPartition =
  | { status: 'none' }
  | { status: 'join-mismatch' }
  | { status: 'applied'; actionIndicesByStep: Map<number, Set<number>> };

const INTERNAL_GROUP_PREFIX = 'Canary Internal:';
const PLAYBOOK_STEP_GROUP = /^Playbook Step (\d+):/;
const RUN_CODE_GROUP = /^Run Code (\d+):/;

export function classifyActionProvenance(
  groups: TraceGroup[],
  action: TraceAction
): ProvenanceClass {
  const groupById = new Map(groups.map((group) => [group.callId, group]));
  let current = action.parentId;
  for (let hops = 0; current && hops < 16; hops++) {
    const group = groupById.get(current);
    if (!group) return 'user';
    if (group.name.startsWith(INTERNAL_GROUP_PREFIX)) return 'internal';
    current = group.parentId;
  }
  return 'user';
}

function parentChain(action: TraceAction, groupById: Map<string, TraceGroup>): string[] {
  const chain: string[] = [];
  let current = action.parentId;
  const seen = new Set<string>();
  for (let hops = 0; current && hops < 32; hops++) {
    if (seen.has(current)) break;
    seen.add(current);
    chain.push(current);
    current = groupById.get(current)?.parentId;
  }
  return chain;
}

export function buildPlaybookStepScope(args: {
  steps: PlaybookStep[];
  linkableSteps: PlaybookStep[];
  segmentActions: Array<{ index: number; action: TraceAction }>;
  groups: TraceGroup[];
  allCandidates: CandidateAction[];
}): PlaybookStepScope {
  const groupById = new Map(args.groups.map((group) => [group.callId, group]));
  const playbookGroups = args.groups.filter((group) => PLAYBOOK_STEP_GROUP.test(group.name));
  if (playbookGroups.length === 0) return { status: 'none' };

  const chainsByActionIndex = new Map(
    args.segmentActions.map((entry) => [entry.index, parentChain(entry.action, groupById)])
  );
  const segmentGroupIds = new Set([...chainsByActionIndex.values()].flat());
  const relevantGroups = playbookGroups
    .map((group) => ({
      group,
      actionIndices: args.segmentActions
        .filter((entry) => chainsByActionIndex.get(entry.index)?.includes(group.callId) ?? false)
        .map((entry) => entry.index),
    }))
    .filter(
      (entry) =>
        entry.actionIndices.length > 0 ||
        (entry.group.parentId !== undefined && segmentGroupIds.has(entry.group.parentId))
    );
  if (relevantGroups.length === 0) return { status: 'none' };

  const stepOrders = new Set(args.steps.map((step) => step.order));
  const seenOrders = new Set<number>();
  const actionOwner = new Map<number, string>();
  const actionsByStep = new Map<number, Set<number>>();

  for (const entry of relevantGroups) {
    const match = PLAYBOOK_STEP_GROUP.exec(entry.group.name);
    const order = match ? Number(match[1]) : NaN;
    if (!Number.isInteger(order) || !stepOrders.has(order)) return { status: 'malformed' };
    if (entry.group.endTime === undefined || entry.actionIndices.length === 0) {
      return { status: 'malformed' };
    }
    if (seenOrders.has(order)) return { status: 'malformed' };
    seenOrders.add(order);

    for (const actionIndex of entry.actionIndices) {
      const existing = actionOwner.get(actionIndex);
      if (existing && existing !== entry.group.callId) return { status: 'malformed' };
      actionOwner.set(actionIndex, entry.group.callId);
    }
    actionsByStep.set(order, new Set(entry.actionIndices));
  }

  const claimedActionIndices = new Set(
    [...actionsByStep.values()].flatMap((indices) => [...indices])
  );
  const allCandidateIndices = args.allCandidates.map((candidate) => candidate.index);
  const candidateIndicesByStep = new Map<number, Set<number>>();
  for (const step of args.linkableSteps) {
    const grouped = actionsByStep.get(step.order);
    if (grouped) {
      candidateIndicesByStep.set(
        step.order,
        new Set(allCandidateIndices.filter((index) => grouped.has(index)))
      );
      continue;
    }
    candidateIndicesByStep.set(
      step.order,
      new Set(allCandidateIndices.filter((index) => !claimedActionIndices.has(index)))
    );
  }

  return { status: 'applied', candidateIndicesByStep };
}

export function buildRunCodeGroupPartition(args: {
  groups: TraceGroup[];
  segmentActions: Array<{ index: number; action: TraceAction }>;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  runCodeToolCalls: RunCodeToolCall[];
}): RunCodeGroupPartition {
  const groupById = new Map(args.groups.map((group) => [group.callId, group]));
  const runCodeGroups = args.groups.filter((group) => RUN_CODE_GROUP.test(group.name));
  if (runCodeGroups.length === 0) return { status: 'none' };

  const chainsByActionIndex = new Map(
    args.segmentActions.map((entry) => [entry.index, parentChain(entry.action, groupById)])
  );
  const relevantGroups = runCodeGroups
    .map((group) => ({
      group,
      actionIndices: args.segmentActions
        .filter((entry) => chainsByActionIndex.get(entry.index)?.includes(group.callId) ?? false)
        .map((entry) => entry.index),
    }))
    .filter((entry) => entry.actionIndices.length > 0);
  if (relevantGroups.length === 0) return { status: 'none' };
  if (relevantGroups.length !== args.runCodeToolCalls.length) return { status: 'join-mismatch' };

  const seenToolCallStepOrders = new Set<number>();
  for (const call of args.runCodeToolCalls) {
    if (seenToolCallStepOrders.has(call.stepOrder)) return { status: 'join-mismatch' };
    seenToolCallStepOrders.add(call.stepOrder);
  }

  const actionOwner = new Map<number, string>();
  const groupsBySeq = new Map<number, (typeof relevantGroups)[number]>();
  for (const entry of relevantGroups) {
    if (entry.group.endTime === undefined) return { status: 'join-mismatch' };
    const match = RUN_CODE_GROUP.exec(entry.group.name);
    const seq = match ? Number(match[1]) : NaN;
    if (!Number.isInteger(seq) || seq < 1 || seq > relevantGroups.length) {
      return { status: 'join-mismatch' };
    }
    if (groupsBySeq.has(seq)) return { status: 'join-mismatch' };
    groupsBySeq.set(seq, entry);
    for (const actionIndex of entry.actionIndices) {
      const existing = actionOwner.get(actionIndex);
      if (existing && existing !== entry.group.callId) return { status: 'join-mismatch' };
      actionOwner.set(actionIndex, entry.group.callId);
    }
  }

  const actionIndicesByStep = new Map<number, Set<number>>();
  for (let seq = 1; seq <= relevantGroups.length; seq++) {
    const group = groupsBySeq.get(seq);
    const call = args.runCodeToolCalls[seq - 1];
    if (!group || !call || !args.parsedRunCodeBySteps.has(call.stepOrder)) {
      return { status: 'join-mismatch' };
    }
    if (actionIndicesByStep.has(call.stepOrder)) return { status: 'join-mismatch' };
    actionIndicesByStep.set(call.stepOrder, new Set(group.actionIndices));
  }

  return { status: 'applied', actionIndicesByStep };
}
